// The admin panel grid is a fixed number of equal tiles across. Most cards are one tile; a
// dangerous-waste card is one tile wide and two tall. A tall card leaves a gap beside it, and
// this places every later card into the first tile it actually fits, so those gaps get
// backfilled instead of leaving a half-empty row.
export const ADMIN_PANEL_COLUMNS = 4;

const CORNER_RADIUS = 10;

const cellKey = (row, column) => `${row},${column}`;

function normalizeRowSpan(rowSpan) {
    const span = Math.floor(Number(rowSpan));
    return Number.isFinite(span) && span > 0 ? span : 1;
}

// The corners a card exposes to the outside of the block, so only the block's own outline is
// rounded and cards that sit against a neighbour stay square against it. A card spanning two
// rows can be exposed on one of its rows and not the other, which is why each side is checked
// at the row that corner belongs to.
function getBorderRadius(occupied, { row, column, rowSpan }) {
    const lastRow = row + rowSpan - 1;
    const isFree = (targetRow, targetColumn) => !occupied.has(cellKey(targetRow, targetColumn));

    const topExposed = isFree(row - 1, column);
    const bottomExposed = isFree(lastRow + 1, column);

    const isTopLeft = topExposed && isFree(row, column - 1);
    const isTopRight = topExposed && isFree(row, column + 1);
    const isBottomRight = bottomExposed && isFree(lastRow, column + 1);
    const isBottomLeft = bottomExposed && isFree(lastRow, column - 1);

    return [isTopLeft, isTopRight, isBottomRight, isBottomLeft]
        .map((isRounded) => `${isRounded ? CORNER_RADIUS : 0}px`)
        .join(' ');
}

/**
 * Places `cards` into a `columns`-wide grid, in order, each into the first tile its full height
 * fits. Returns them with `row`, `column`, `rowSpan`, `borderRadius` and `isLastColumn` added.
 */
export function layoutAdminPanelCards(cards = [], columns = ADMIN_PANEL_COLUMNS) {
    if (!Array.isArray(cards) || cards.length === 0) return [];

    const occupied = new Set();

    const placed = cards.map((card) => {
        const rowSpan = normalizeRowSpan(card?.rowSpan);
        const fitsAt = (row, column) => {
            for (let offset = 0; offset < rowSpan; offset += 1) {
                if (occupied.has(cellKey(row + offset, column))) return false;
            }
            return true;
        };

        let row = 0;
        let column = -1;
        while (column === -1) {
            for (let candidate = 0; candidate < columns; candidate += 1) {
                if (fitsAt(row, candidate)) {
                    column = candidate;
                    break;
                }
            }
            if (column === -1) row += 1;
        }

        for (let offset = 0; offset < rowSpan; offset += 1) {
            occupied.add(cellKey(row + offset, column));
        }

        return { ...card, row, column, rowSpan };
    });

    // Radii are only decided once every card is down, since a card's corner depends on the
    // neighbours that land around it later.
    return placed.map((card) => ({
        ...card,
        borderRadius: getBorderRadius(occupied, card),
        isLastColumn: card.column === columns - 1,
    }));
}
